<template>
  <div data-locator="features">
    <div class="text-body-1 font-weight-bold pb-5 ml-1">
      {{ $t('project.project-settings.ProjectSettingsFeatures.title') }}
    </div>

    <!-- NDA -->
    <AppCard class="ma-1" data-locator="nda-settings">
      <div class="d-flex align-center">
        <p class="text--medium text--bold">
          {{ $t('project.project-settings.ProjectSettingsFeatures.nda.title') }}
        </p>

        <template v-if="hasNda">
          <app-icon-badge color="success"
                          class="mb-2 ml-3 mr-2"
                          :bordered="false"
                          dot
          >
          </app-icon-badge>
          <p class="text-success text--small text--bold">
            {{ $t("project.project-settings.ProjectSettingsFeatures.nda.activated") }}
          </p>
        </template>
      </div>

      <div class="d-flex justify-space-between align-center mt-2">
        <p class="ma-0 text--small">
          {{ $t('project.project-settings.ProjectSettingsFeatures.nda.ndaLabel') }}
        </p>

        <app-button
          v-if="!hasNda"
          data-locator="activate-button"
          type="outlined"
          variant="neutral"
          :disabled="toggleNdaLoading"
          :loading="toggleNdaLoading"
          @click="isNdaActivationDialogOpen = true"
        >
          {{ $t('common.activate') }}...
        </app-button>
        <app-button
          v-else
          data-locator="delete-button"
          type="outlined"
          variant="neutral"
          :disabled="toggleNdaLoading"
          :loading="toggleNdaLoading"
          @click="isNdaDeletionDialogOpen = true"
        >
          {{ $t("common.delete") }}
        </app-button>
      </div>
    </AppCard>

    <NdaDialog
      v-if="isNdaActivationDialogOpen"
      :mnemo="mnemo"
      @close="isNdaActivationDialogOpen = false"
      @nda-added="isNdaAddedSignDialogOpen = true"
    />
    <NdaDeletionDialog
      v-if="isNdaDeletionDialogOpen"
      @close="isNdaDeletionDialogOpen = false"
      @confirm="prepareToggleNdaDataroomAccess"
    />
    <NdaAddedSignDialog
      v-if="isNdaAddedSignDialogOpen"
      :mnemo="mnemo"
      @close="isNdaAddedSignDialogOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ComputedRef, ref } from 'vue'
import { useStore } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import { toast } from '@/common/utils/toast'
import i18n from '@/plugins/i18n'
import NdaAddedSignDialog from '@/project/nda/dialogs/NdaAddedSignDialog.vue'
import NdaDeletionDialog from '@/project/nda/dialogs/NdaDeletionDialog.vue'
import NdaDialog from '@/project/nda/dialogs/NdaDialog.vue'
import { PATCH_ROOM } from '@/store/modules/room/action_types'
import { SET_CURRENT_ROOM } from '@/store/modules/room/mutation_types'

  export default defineComponent({
    name: 'ProjectSettingsDataroomFeatures',
    components: {
      NdaDialog,
      NdaDeletionDialog,
      NdaAddedSignDialog,
      AppCard,
    },
    props: {
      mnemo: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const store = useStore()
      const currentRoom = computed(() => store.state.room.currentRoom)
      const hasNda: ComputedRef<boolean | undefined> = computed(() => currentRoom.value?.hasNda)
      const isNdaActivationDialogOpen = ref(false)
      const isNdaDeletionDialogOpen = ref(false)
      const isNdaAddedSignDialogOpen = ref(false)
      const toggleNdaLoading = ref(false)

      const prepareToggleNdaDataroomAccess = async (): Promise<void> => {
        try {
          toggleNdaLoading.value = true
          const newValue = !hasNda.value
          await store.dispatch(`room/${PATCH_ROOM}`, {
            mnemo: props.mnemo,
            data: {
              optNda: newValue,
            },
          })
          if (currentRoom.value) {
            currentRoom.value.hasNda = !hasNda.value
            store.commit(`room/${SET_CURRENT_ROOM}`, currentRoom.value)
          }
          if (newValue) {
            toast.success(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.ndaActivationSuccess'))
          } else {
            toast.success(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.ndaDeactivationSuccess'))
          }
        } catch (error) {
          console.error(error)
          toast.error(i18n.global.t('project.project-settings.ProjectSettingsFeatures.nda.sndaActivationError'))
        } finally {
          toggleNdaLoading.value = false
        }
      }

      return {
        hasNda,
        toggleNdaLoading,
        isNdaActivationDialogOpen,
        isNdaDeletionDialogOpen,
        isNdaAddedSignDialogOpen,
        prepareToggleNdaDataroomAccess,
      }
    },
  })
</script>
